import React, { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, styled } from "@mui/material";

import { graphql } from "@/gql";
import {
  ChatMessageFragment,
  DeleteMessageInput,
  MessageChannel,
  UpdateMessageInput,
} from "@/gql/graphql";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import If from "@components/layout/If";

import ChatEmptyView from "./ChatEmptyView";
import Message from "./Message";
import MessageList from "./MessageList";
import Toolbar from "./Toolbar";
import { useReadMessages } from "../../api";

interface Props {
  chatId: string;
  channel: MessageChannel;
}

const Root = styled("div")(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  overflowY: "hidden",
}));

const ReplyContainer = styled(Box)({
  padding: "1rem 1rem 0",
  backgroundColor: colors.white,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const ReplyTextContainer = styled(Box)({
  padding: 12,
  backgroundColor: "#DEE6F0",
  borderRadius: theme.spacing(2),
  borderTopLeftRadius: 0,
  whiteSpace: "pre-wrap",
});

const defaultValue: UpdateMessageInput = {
  id: "",
  patch: { body: "" },
};

const chatQuery = graphql(`
  query ChatChannelMessages(
    $chatId: ID!
    $channel: MessageChannel!
    $after: String
  ) {
    chat(id: $chatId) {
      id
      messages(channel: $channel, first: 100, after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...ChatMessage
          }
        }
      }
    }
  }
`);

const deleteMessageMutation = graphql(`
  mutation DeleteMessage($input: DeleteMessageInput!) {
    deleteMessage(input: $input) {
      message {
        ...ChatMessage
      }
    }
  }
`);
const updateMessageMutation = graphql(`
  mutation UpdateMessage($input: UpdateMessageInput!) {
    updateMessage(input: $input) {
      message {
        ...ChatMessage
      }
    }
  }
`);

const Chat: React.FC<Props> = ({ chatId, channel }) => {
  const endCursor = useRef<string | null | undefined>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [replyMessage, setReplyMessage] =
    useState<ChatMessageFragment | null>(null);
  const [updatedMessage, setUpdatedMessage] =
    useState<UpdateMessageInput>(defaultValue);

  const { loading, data, fetchMore } = useQuery(chatQuery, {
    variables: { chatId, channel },
    onCompleted: (data) => {
      if (data.chat == null) return;

      endCursor.current = data.chat.messages.pageInfo.endCursor;
      setHasNextPage(data.chat.messages.pageInfo.hasNextPage);
    },
  });
  const setMessageRead = useReadMessages(chatId, channel);

  const [deleteMessage] = useMutation(deleteMessageMutation);
  const [updateMessage] = useMutation(updateMessageMutation);

  const handleDeleteMessage = (id: DeleteMessageInput) => {
    deleteMessage({ variables: { input: id } });
  };

  const handleEditMessage = (message: UpdateMessageInput) => {
    if (message.id === "") return;
    updateMessage({ variables: { input: message } });
  };

  const _handleEdit = () => handleEditMessage(updatedMessage);
  const handleRemoveReply = () => setReplyMessage(null);

  useKeyboardShortcut("Enter", _handleEdit);

  const loadMoreItems = useCallback(async () => {
    if (hasNextPage && !loading) {
      return fetchMore({ variables: { after: endCursor.current } }).then(
        (result) => {
          const newItemsCount = result.data.chat?.messages.edges.length ?? 0;
          return newItemsCount;
        }
      );
    } else {
      return Promise.resolve(0);
    }
  }, [hasNextPage, loading]);

  useEffect(() => {
    setReplyMessage(null);
  }, [channel]);

  const messages = data?.chat?.messages.edges ?? [];

  return (
    <Root>
      <If
        condition={messages.length === 0}
        then={<ChatEmptyView />}
        else={
          <MessageList
            messagesCount={messages.length}
            renderMessage={(index) => {
              const message = messages[index]?.node;
              if (message == null) return null;
              return (
                <Message
                  messageId={message.id}
                  onRead={setMessageRead}
                  onReply={() => setReplyMessage(message)}
                  onEdit={(body) =>
                    setUpdatedMessage({
                      id: message.id,
                      patch: { body },
                    })
                  }
                  onDelete={() => handleDeleteMessage({ id: message.id })}
                />
              );
            }}
            loadMore={loadMoreItems}
          />
        }
      />
      {replyMessage && (
        <ReplyContainer>
          <Box display="flex" flexDirection="column">
            Replying to
            <ReplyTextContainer>{replyMessage.body}</ReplyTextContainer>
          </Box>
          <IconButton onClick={handleRemoveReply}>
            <CloseIcon fontSize="small" sx={{ color: colors.gray }} />
          </IconButton>
        </ReplyContainer>
      )}
      <Toolbar
        chatId={chatId}
        channel={channel}
        messageId={replyMessage?.id}
        onSend={handleRemoveReply}
      />
    </Root>
  );
};

export default Chat;
